import React, { useState, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { addressSchema, type AddressFormData } from '@/lib/validations/address';
import { AddressesApi } from '@/lib/Apis/apis/addresses-api';
import { Address } from '@/lib/Apis';
import { AXIOS_CONFIG } from '@/lib/Apis/wrapper';
import { useNotification } from '@/providers/NotificationProvider';
import { formatPhoneNumber } from '@/lib/utils/format';
import { US_STATES } from '@/lib/constants/us-states';

interface AddressFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAddressAdded?: (address: any) => void;
  onAddressUpdated?: (address: any) => void;
  initialAddress?: Address | null;
}

export function AddressFormModal({ isOpen, onClose, onAddressAdded, onAddressUpdated, initialAddress }: AddressFormModalProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const notification = useNotification();

  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: { errors }
  } = useForm<AddressFormData>({
    resolver: zodResolver(addressSchema),
    defaultValues: {
      name: initialAddress?.name || '',
      phone: initialAddress?.phone || '',
      street1: initialAddress?.street1 || '',
      street2: initialAddress?.street2 || '',
      city: initialAddress?.city || '',
      state: initialAddress?.state || '',
      zip: initialAddress?.zip || '',
      country: initialAddress?.country || 'United States',
    }
  });

  // Reset form when switching between create/edit
  useEffect(() => {
    reset({
      name: initialAddress?.name || '',
      phone: initialAddress?.phone || '',
      street1: initialAddress?.street1 || '',
      street2: initialAddress?.street2 || '',
      city: initialAddress?.city || '',
      state: initialAddress?.state || '',
      zip: initialAddress?.zip || '',
      country: initialAddress?.country || 'United States',
    });
  }, [initialAddress, reset]);

  const onSubmit = async (data: AddressFormData) => {
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      const api = new AddressesApi(AXIOS_CONFIG);
      if (initialAddress?.id) {
        const response = await api.updateUserAddress({
          name: data.name,
          street1: data.street1,
          street2: data.street2,
          city: data.city,
          state: data.state,
          zip: data.zip,
          country: data.country,
          phone: data.phone?.replace(/[^\d+]/g, ''), // Strip formatting chars except + for backend
        }, initialAddress.id);
        notification.success(
          'Address updated',
          'Your address has been updated successfully.'
        );
        reset();
        onClose();
        if (onAddressUpdated) onAddressUpdated(response.data);
      } else {
        const response = await api.createAddressForUser({
          name: data.name,
          street1: data.street1,
          street2: data.street2,
          city: data.city,
          state: data.state,
          zip: data.zip,
          country: data.country,
          phone: data.phone?.replace(/[^\d+]/g, ''), // Strip formatting chars except + for backend
        });
        if (response.status === 201) {
          notification.success(
            'Address added',
            'Your new address has been saved to your account.'
          );
          reset();
          onClose();
          if (onAddressAdded) onAddressAdded(response.data);
        } else {
          setSubmitError('We could not save this address. Please try again.');
        }
      }
    } catch (error: any) {
      setSubmitError(error?.response?.data?.message || 'Something went wrong while saving your address. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={initialAddress ? 'Edit Address' : 'Add New Address'}
      size="lg"
    >
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label={<><span className="text-gray-700">Full name</span><span className="text-red-500 px-1">*</span></>}
            placeholder="John Doe"
            {...register('name')}
            maxLength={100}
            error={errors.name?.message}
          />
          <Controller
            name="phone"
            control={control}
            render={({ field: { onChange, value } }) => (
              <Input
                label={<><span className="text-gray-700">Phone number</span><span className="text-red-500 px-1">*</span></>}
                placeholder="+1 415 555 0100"
                value={value}
                onChange={(e) => {
                  const formatted = formatPhoneNumber(e.target.value);
                  onChange(formatted);
                }}
                error={errors.phone?.message}
              />
            )}
          />
          <div className="md:col-span-2">
            <Input
              label={<><span className="text-gray-700">Address line 1</span><span className="text-red-500 px-1">*</span></>}
              placeholder="123 Main St"
              {...register('street1')}
              maxLength={200}
              error={errors.street1?.message}
            />
          </div>
          <div className="md:col-span-2">
            <Input
              label="Address line 2 (optional)"
              placeholder="Apt 4B"
              {...register('street2')}
              maxLength={200}
            />
          </div>
          <Input
            label={<><span className="text-gray-700">City</span><span className="text-red-500 px-1">*</span></>}
            placeholder="New York"
            {...register('city')}
            maxLength={100}
            error={errors.city?.message}
          />
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <span className="text-gray-700">State</span><span className="text-red-500 px-1">*</span>
            </label>
            <select
              {...register('state')}
              className={`w-full px-4 py-3 rounded-lg border-2 transition-all duration-200 focus:outline-hidden focus:ring-4 text-sm bg-white ${errors.state ? 'border-red-500 focus:border-red-600 focus:ring-red-500/20' : 'border-gray-200 focus:border-hprimary-500 focus:ring-hprimary-500/20'}`}
            >
              <option value="">Select state</option>
              {US_STATES.map(s => (
                <option key={s.abbr} value={s.abbr}>{s.name}</option>
              ))}
            </select>
            {errors.state && <p className="mt-2 text-sm text-red-600">{errors.state.message}</p>}
          </div>
          <Input
            label={<><span className="text-gray-700">ZIP code</span><span className="text-red-500 px-1">*</span></>}
            placeholder="10001"
            {...register('zip')}
            maxLength={20}
            error={errors.zip?.message}
          />
          <Input
            label={<><span className="text-gray-700">Country</span></>}
            {...register('country')}
            disabled
            className="bg-gray-50 text-gray-500 cursor-not-allowed"
          />
        </div>
        {submitError && (
          <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-xl">
            <AlertCircle className="w-4 h-4 text-red-500 px-1 shrink-0 mt-0.5" />
            <p className="text-sm text-red-700">{submitError}</p>
          </div>
        )}
        <div className="flex justify-end gap-4">
          <button
            type="button"
            onClick={onClose}
            className='flex flex-row items-center gap-2 px-6 py-2 border border-slate-200 rounded-xl text-slate-700 hover:opacity-80 transition-colors text-hsecondary text-sm'
          >
            Cancel
          </button>
          <button type="submit" disabled={isSubmitting} className='flex flex-row items-center gap-2 px-6 py-2 border border-slate-200 rounded-xl text-slate-700 hover:opacity-80 transition-colors bg-hsecondary text-white text-sm'>
            {isSubmitting ? initialAddress?.id ? 'Updating Address...' : 'Adding Address...' : initialAddress?.id ? 'Update Address' : 'Add Address'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
